import React, { useEffect, useState } from "react";

import { getFileExtension } from "../../../utils/strings";
import { formatBytes, convertMegabytesToBytes } from "../../../utils/numbers";
import { parseForwardProps } from "../../../utils/styled";
import { hideScrollbar } from "../../../theme/mixins";

import { useSnackbar } from "contexts";
import {
  FileTextIcon,
  ImageIcon,
  CloudUploadIcon,
  XIcon,
  GripVerticalIcon,
} from "lucide-react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  type DraggableProvided,
  type DraggableStateSnapshot,
  type DroppableProvided,
  type DroppableStateSnapshot,
  type DraggableLocation,
} from "@hello-pangea/dnd";
import { useDropzone, type FileWithPath } from "react-dropzone";
import { v4 as uuid } from "uuid";

import {
  Stack,
  Box,
  Typography,
  Grid,
  Button,
  IconButton,
  FormControl,
  styled,
  useTheme,
  Zoom,
  CircularProgress,
} from "@mui/material";

import { Image } from "../../display/Image";

interface FileUploadProps {
  items?: string[];
  label?: string;
  description: string;
  onFilesChange?: (files: File[]) => void;
  acceptFiles: any;
  showMaxFileSize?: boolean;
}

type File = {
  id: string;
  file: FileWithPath;
  preview_url: string;
  isUploaded: boolean;
};

const reorder = (items: any[], startIndex: number, endIndex: number) => {
  const result = Array.from(items);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

export const FileUpload = ({
  items,
  label,
  description,
  onFilesChange,
  acceptFiles = {},
  showMaxFileSize = false,
}: FileUploadProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [isItemsLoading, setIsItemsLoading] = useState(true);
  const theme = useTheme();

  const { open: notification } = useSnackbar();

  useEffect(() => {
    onFilesChange?.(files);
  }, [files, onFilesChange]);

  useEffect(() => {
    if (items && items.length > 0) {
      try {
        getImages(items);
      } catch (error) {
        console.error(error);
      }
    } else {
      setIsItemsLoading(false);
    }
  }, [items]);

  const getBlobImages = async (items: string[]) => {
    const images = await Promise.all(
      items.map(async (image: string) => {
        const file = await fetch(image)
          .then((res) => res.blob())
          .then((blob) => {
            const filename = image.substring(image.lastIndexOf("/") + 1);
            return new File([blob], filename, { type: blob.type });
          });

        return {
          id: uuid(),
          file,
          preview_url: image,
          isUploaded: true,
        };
      })
    );
    return images;
  };

  const getImages = async (items: string[]) => {
    setIsItemsLoading(true);
    const images = await getBlobImages(items);
    setFiles(images);
    setIsItemsLoading(false);
  };

  const {
    getRootProps,
    getInputProps,
    open,
    isFocused,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept: acceptFiles,
    noClick: true,
    noKeyboard: true,
    maxSize: convertMegabytesToBytes(50),
    onDrop: (acceptedFiles, rejectedFiles) => {
      const acceptedFilesWithPreview = acceptedFiles.map((file) => ({
        id: uuid(),
        file,
        preview_url: URL.createObjectURL(file),
        isUploaded: false,
      }));

      if (acceptedFilesWithPreview.length > 0) {
        setFiles((state) => [...state, ...acceptedFilesWithPreview]);
      }

      if (rejectedFiles.length > 0) {
        const { errors } = rejectedFiles[0];

        errors.forEach((error) => {
          if (error.code === "file-too-large") {
            notification({
              type: "error",
              title: "File is too large. Max file size 50MB.",
            });
          }

          if (error.code === "file-invalid-type") {
            notification({
              type: "error",
              title:
                'You can upload "heic", "png", "jpg", "jpeg" file formats only',
            });
          }
        });
      }
    },
  });

  useEffect(() => {
    // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
    return () => files.forEach((file) => URL.revokeObjectURL(file.preview_url));
  }, []);

  const handleDragEnd = ({
    destination,
    source,
  }: {
    destination: DraggableLocation;
    source: DraggableLocation;
  }) => {
    if (!destination) return;

    setFiles((files) => reorder(files, source.index, destination.index));
  };

  const handleDeleteItem = (id: string) => {
    setFiles((files) => {
      return files.filter((file) => file.id !== id);
    });
  };

  return (
    // TODO: Break this out into FIleUploadField under custom Fields and keep the dropZone component here under common
    <FormControl>
      {label && (
        <Typography mb={4} variant="body2">
          {label}
        </Typography>
      )}
      <Grid item md={12} sm={12}>
        <StyledDropzone
          {...getRootProps({ isFocused, isDragAccept, isDragReject })}
          onClick={open}
          sx={{
            borderRadius: 2,
            border: `1px dashed ${theme.palette.grey[400]}`,
            cursor: "pointer",
            height: "268px",
            width: "100%",
            backgroundColor: theme.palette.grey[50],
            "&:hover": {
              border: `1px dashed ${theme.palette.brand[600]}`,
            },
          }}
        >
          <input {...getInputProps()} />
          {isItemsLoading ? (
            <CircularProgress color="inherit" />
          ) : (
            <Stack
              spacing="12px"
              alignItems="center"
              justifyContent="center"
              sx={{ height: "100%" }}
            >
              <CloudUploadIcon size={32} strokeWidth={1} />

              <Typography variant="body2">
                <span style={{ textDecoration: "underline" }}>Click here</span>
                <span> or drop files to upload</span>
              </Typography>

              <Typography variant="body3" color="text.secondary">
                {description}
              </Typography>
              {showMaxFileSize && (
                <Typography variant="body3" color="text.secondary">
                  (Max file size: 5MB)
                </Typography>
              )}
            </Stack>
          )}
        </StyledDropzone>
      </Grid>
      <Grid item md={12} sm={12}>
        {files.length > 0 && (
          <FileList
            items={files}
            onDragEnd={handleDragEnd}
            onDeleteFile={handleDeleteItem}
          />
        )}
      </Grid>
    </FormControl>
  );
};

const FileList = ({ items = [], onDragEnd, onDeleteFile }: any) => {
  const deleteFile = (id: string) => {
    onDeleteFile(id);
  };

  const theme = useTheme();
  const status = "Complete";

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable">
        {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => (
          <Stack
            justifyContent="flex-start"
            ref={provided.innerRef}
            {...provided.droppableProps}
            // style={{
            //   background: snapshot.isDraggingOver ? theme.palette.grey[50] : 'transparent',
            // }}
            sx={{
              maxHeight: "320px",
              minHeight: items.length * 106,
              overflowY: "auto",
              overflowX: "hidden",
              flexShrink: 0,
              ...hideScrollbar,
            }}
          >
            {items.map((item: any, index: number) => (
              <Draggable key={item.id} index={index} draggableId={item.id}>
                {(
                  provided: DraggableProvided,
                  snapshot: DraggableStateSnapshot
                ) => {
                  return (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...provided.draggableProps.style,
                        marginTop: "16px",
                        background: snapshot.isDragging
                          ? theme.palette.white[100]
                          : "transparent",
                      }}
                    >
                      {/* <Zoom in={true}> */}
                      <Grid
                        {...provided.dragHandleProps}
                        container
                        direction="row"
                        alignItems="center"
                        spacing={4}
                        sx={{
                          border: `1px solid ${theme.palette.grey[200]}`,
                          borderRadius: 2,
                          p: 3,
                          backgroundColor: "bgColor.main",
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between">
                          <Stack
                            direction="row"
                            spacing={4}
                            alignItems="center"
                            sx={{ maxWidth: 360 }}
                          >
                            <Stack>
                              <GripVerticalIcon strokeWidth={1} />
                            </Stack>
                            <Stack
                              alignItems="center"
                              justifyContent="center"
                              flexShrink={0}
                              sx={(theme) => ({
                                height: 64,
                                width: 64,
                                overflow: "hidden",
                                border: `1px solid ${theme.palette.grey[300]}`,
                                borderRadius: 2,
                                backgroundColor: theme.palette.grey[50],
                              })}
                            >
                              {["jpg", "jpeg", "png", "gif", "heic"].some(
                                (fileType) =>
                                  item.file.name
                                    .toLowerCase()
                                    .includes(fileType)
                              ) ? (
                                item.preview_url ? (
                                  <Image
                                    sx={{ height: 64, width: 64, minWidth: 64 }}
                                    src={item.preview_url}
                                    fit="cover"
                                    onLoad={() => {
                                      URL.revokeObjectURL(item.preview_url);
                                    }}
                                  />
                                ) : (
                                  <ImageIcon strokeWidth={1} />
                                )
                              ) : (
                                <>
                                  <FileTextIcon strokeWidth={1} />
                                  <Typography noWrap variant="body3">
                                    {getFileExtension(item.file.name)}
                                  </Typography>
                                </>
                              )}
                            </Stack>

                            <Stack spacing={2}>
                              <Typography
                                sx={{ maxWidth: "250px" }}
                                variant="body2"
                                noWrap
                              >
                                {item.file.name}
                              </Typography>
                              <Typography
                                variant="body3"
                                color="text.secondary"
                              >
                                ({formatBytes(item.file.size)})<span> • </span>
                                {status}
                              </Typography>
                            </Stack>
                          </Stack>

                          <Stack>
                            <IconButton
                              onClick={() => deleteFile(item.id)}
                              sx={{
                                marginLeft: 4,
                                "&:hover": {
                                  color: theme.palette.error.main,
                                },
                              }}
                            >
                              <XIcon strokeWidth={1} />
                            </IconButton>
                            {/* {isItemLoading && <Spinner size={24} />} */}
                          </Stack>
                        </Stack>
                      </Grid>
                      {/* </Zoom> */}
                    </div>
                  );
                }}
              </Draggable>
            ))}
          </Stack>
        )}
      </Droppable>
    </DragDropContext>
  );
};

const getColor = (props: any) => {
  if (props.isDragAccept) {
    return "#00e676";
  }
  if (props.isDragReject) {
    return "#ff1744";
  }
  if (props.isFocused) {
    return "#2196f3";
  }
  return "#eeeeee";
};

const StyledDropzone = styled(Box, {
  shouldForwardProp: parseForwardProps([
    "isDragAccept",
    "isDragReject",
    "isFocused",
  ]),
})(({ theme, ...rest }) => ({
  width: "100%",
  height: "100%",
  border: `1px dashed ${theme.palette.grey[200]}`,
  borderColor: getColor(rest),
  padding: "36px 0",
  textAlign: "center",
}));
